<template>
  <BModal
    :model-value="modelValue"
    title="Select Customer Contact"
    size="lg"
    hide-footer
    no-focus
    no-fade
    @update:model-value="$emit('update:modelValue', $event)"
    @hidden="$emit('update:modelValue', false)"
  >
    <div class="find-contact-modal">
      <div class="d-flex justify-content-between align-items-center gap-3 mb-3">
        <div>
          <div class="fw-semibold">{{ customerName || customerId || 'Customer' }}</div>
          <small class="text-muted">Choose a saved contact, or close this window and enter details manually.</small>
        </div>
        <span class="badge text-bg-light border">{{ filteredContacts.length }} contact{{ filteredContacts.length === 1 ? '' : 's' }}</span>
      </div>

      <div class="input-group mb-3">
        <span class="input-group-text"><i class="ri-search-line" /></span>
        <input v-model="search" type="search" class="form-control" placeholder="Search name, phone, email, or department">
      </div>

      <div v-if="loading" class="py-5 text-center text-muted"><span class="spinner-border spinner-border-sm me-2" />Loading customer contacts…</div>
      <div v-else-if="!customerId" class="py-4 text-center text-muted">Select a customer first, then choose a contact.</div>
      <div v-else-if="!filteredContacts.length" class="py-4 text-center text-muted">No saved contacts found. You may enter the contact manually.</div>
      <div v-else class="list-group find-contact-list">
        <button v-for="contact in filteredContacts" :key="contact.customer_contact_id || contact.id" type="button" class="list-group-item list-group-item-action" @click="selectContact(contact)">
          <div class="d-flex align-items-center justify-content-between gap-3">
            <div class="text-start">
              <div class="fw-semibold">{{ contact.contact_name }} <span v-if="Number(contact.default) === 1" class="badge text-bg-primary ms-1">Default</span></div>
              <small class="text-muted">{{ [contact.contact_no, contact.email, contact.department].filter(Boolean).join(' · ') }}</small>
            </div>
            <i class="ri-arrow-right-line fs-5" />
          </div>
        </button>
      </div>
    </div>
  </BModal>
</template>

<script>
import axios from 'axios';

export default {
  name: 'FindContactModal',
  props: {
    modelValue: { type: Boolean, default: false },
    customerId: { type: [String, Number], default: null },
    customerName: { type: String, default: '' },
  },
  emits: ['update:modelValue', 'select'],
  data() {
    return { contacts: [], loading: false, search: '' };
  },
  computed: {
    filteredContacts() {
      const query = this.search.trim().toLowerCase();
      if (!query) return this.contacts;
      return this.contacts.filter((contact) => [contact.contact_name, contact.contact_no, contact.email, contact.department, contact.job_title]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query)));
    },
  },
  watch: {
    customerId() {
      if (this.modelValue) this.loadContacts();
    },
    modelValue(value) {
      if (value) this.loadContacts();
      else this.search = '';
    },
  },
  methods: {
    async loadContacts() {
      if (!this.customerId) {
        this.contacts = [];
        return;
      }
      this.loading = true;
      try {
        const response = await axios.get(`${process.env.VUE_APP_AUTHDOMAIN}/api/v1/customer_contact/${encodeURIComponent(this.customerId)}`);
        this.contacts = response.data?.data || [];
      } catch (error) {
        console.error('Failed to load customer contacts:', error);
        this.contacts = [];
      } finally {
        this.loading = false;
      }
    },
    selectContact(contact) {
      this.$emit('select', {
        ...contact,
        contact_name: contact.contact_name || contact.name || contact.contact_person || '',
        contact_no: contact.contact_no || contact.contact_number || contact.phone || '',
      });
      this.$emit('update:modelValue', false);
    },
  },
};
</script>

<style scoped>
.find-contact-modal { color: var(--vz-body-color); }
.find-contact-list { max-height: 360px; overflow-y: auto; }
</style>
